import type { DraftOp } from '@/api/types'
import type { MetadataV2 } from '@/services/metadata/keyed'

export function applyDraftOps(metadata: MetadataV2, ops: DraftOp[]): MetadataV2 {
  const next = structuredClone(metadata) as MetadataV2
  for (const op of ops) {
    applyDraftOp(next, op)
  }
  sanitizeOrders(next)
  return next
}

/**
 * Reanexa às listas de ordem os ids que existem no mapa mas ficaram de fora —
 * mesma regra do `_sanitize_order` do backend (mantidos + faltantes ordenados).
 *
 * Sem isso, uma op de ordenação gerada a partir de um estado desatualizado
 * (`columnOrder.set` sem a coluna que outro usuário acabou de criar) deixaria a
 * entidade órfã: presente no mapa, invisível na renderização. Replicar a regra
 * aqui também mantém o espelho local fiel ao que o servidor gravou.
 */
function sanitizeOrders(meta: MetadataV2): void {
  meta.tableOrder = repairOrder(meta.tableOrder, meta.tables)
  meta.relationshipOrder = repairOrder(meta.relationshipOrder, meta.relationships)
  meta.groupOrder = repairOrder(meta.groupOrder, meta.groups)
  for (const table of Object.values(meta.tables)) {
    table.colOrder = repairOrder(table.colOrder, table.cols)
    table.indexOrder = repairOrder(table.indexOrder, table.indexes)
  }
}

function repairOrder(order: string[], items: Record<string, unknown>): string[] {
  const kept = (order ?? []).filter((id) => id in items)
  const missing = Object.keys(items).filter((id) => !kept.includes(id)).sort()
  return [...kept, ...missing]
}

export function applyDraftOp(meta: MetadataV2, op: DraftOp) {
  const opName = op.op
  const handlers: Record<string, (meta: MetadataV2, op: DraftOp) => void> = {
    'project.update': applyProjectUpdate,
    'settings.update': applySettingsUpdate,
    'table.upsert': applyTableUpsert,
    'table.remove': applyTableRemove,
    'table.move': applyTableMove,
    'tableOrder.set': applyTableOrderSet,
    'column.upsert': applyColumnUpsert,
    'column.remove': applyColumnRemove,
    'columnOrder.set': applyColumnOrderSet,
    'index.upsert': applyIndexUpsert,
    'index.remove': applyIndexRemove,
    'indexOrder.set': applyIndexOrderSet,
    'rel.upsert': applyRelUpsert,
    'rel.remove': applyRelRemove,
    'relOrder.set': applyRelOrderSet,
    'group.upsert': applyGroupUpsert,
    'group.remove': applyGroupRemove,
    'groupOrder.set': applyGroupOrderSet,
  }
  const handler = handlers[opName]
  if (!handler) throw new Error(`Unsupported op '${opName}'.`)
  handler(meta, op)
}

function applyProjectUpdate(meta: MetadataV2, op: DraftOp) {
  meta.project = { ...meta.project, ...(op.fields ?? {}) }
}

function applySettingsUpdate(meta: MetadataV2, op: DraftOp) {
  meta.settings = { ...meta.settings, ...(op.fields ?? {}) }
}

function applyTableUpsert(meta: MetadataV2, op: DraftOp) {
  const tableId = requireString(op, 'id')
  if (!meta.tables[tableId]) {
    meta.tables[tableId] = {
      id: tableId,
      schema: '',
      name: '',
      fullName: '',
      comment: '',
      g: '',
      cols: {},
      colOrder: [],
      indexes: {},
      indexOrder: [],
    }
    appendOnce(meta.tableOrder, tableId)
  }
  meta.tables[tableId] = { ...meta.tables[tableId], ...(op.fields ?? {}) }
}

function applyTableRemove(meta: MetadataV2, op: DraftOp) {
  const tableId = requireString(op, 'id')
  if (!meta.tables[tableId]) return
  delete meta.tables[tableId]
  meta.tableOrder = meta.tableOrder.filter((id) => id !== tableId)
  delete meta.layout.positions[tableId]
  for (const [relId, rel] of Object.entries(meta.relationships)) {
    if (rel.from === tableId || rel.to === tableId) delete meta.relationships[relId]
  }
  meta.relationshipOrder = meta.relationshipOrder.filter((id) => meta.relationships[id])
}

function applyTableMove(meta: MetadataV2, op: DraftOp) {
  const tableId = requireString(op, 'id')
  if (!meta.tables[tableId]) throw new Error(`Table '${tableId}' not found.`)
  meta.layout.positions[tableId] = { x: op.x ?? 0, y: op.y ?? 0 }
}

function applyTableOrderSet(meta: MetadataV2, op: DraftOp) {
  meta.tableOrder = [...(op.order ?? [])]
}

function applyColumnUpsert(meta: MetadataV2, op: DraftOp) {
  const table = requireTable(meta, op.tableId)
  const columnId = requireString(op, 'id')
  if (!table.cols[columnId]) {
    table.cols[columnId] = {
      id: columnId,
      key: '',
      pk: false,
      fk: false,
      uq: false,
      name: '',
      type: '',
      logicalType: '',
      notNull: false,
      blank: false,
      desc: '',
    }
    appendOnce(table.colOrder, columnId)
  }
  table.cols[columnId] = { ...table.cols[columnId], ...(op.fields ?? {}) }
}

function applyColumnRemove(meta: MetadataV2, op: DraftOp) {
  const table = requireTable(meta, op.tableId)
  const columnId = requireString(op, 'id')
  if (!table.cols[columnId]) return
  delete table.cols[columnId]
  table.colOrder = table.colOrder.filter((id) => id !== columnId)
}

function applyColumnOrderSet(meta: MetadataV2, op: DraftOp) {
  const table = requireTable(meta, op.tableId)
  table.colOrder = [...(op.order ?? [])]
}

function applyIndexUpsert(meta: MetadataV2, op: DraftOp) {
  const table = requireTable(meta, op.tableId)
  const indexId = requireString(op, 'id')
  if (!table.indexes[indexId]) {
    table.indexes[indexId] = { id: indexId, name: '', cols: [], type: '', unique: false }
    appendOnce(table.indexOrder, indexId)
  }
  table.indexes[indexId] = { ...table.indexes[indexId], ...(op.fields ?? {}) }
}

function applyIndexRemove(meta: MetadataV2, op: DraftOp) {
  const table = requireTable(meta, op.tableId)
  const indexId = requireString(op, 'id')
  if (!table.indexes[indexId]) return
  delete table.indexes[indexId]
  table.indexOrder = table.indexOrder.filter((id) => id !== indexId)
}

function applyIndexOrderSet(meta: MetadataV2, op: DraftOp) {
  const table = requireTable(meta, op.tableId)
  table.indexOrder = [...(op.order ?? [])]
}

function applyRelUpsert(meta: MetadataV2, op: DraftOp) {
  const relId = requireString(op, 'id')
  if (!meta.relationships[relId]) {
    meta.relationships[relId] = { id: relId, from: '', to: '', col: '', type: '1-N' }
    appendOnce(meta.relationshipOrder, relId)
  }
  meta.relationships[relId] = { ...meta.relationships[relId], ...(op.fields ?? {}) }
}

function applyRelRemove(meta: MetadataV2, op: DraftOp) {
  const relId = requireString(op, 'id')
  if (!meta.relationships[relId]) return
  delete meta.relationships[relId]
  meta.relationshipOrder = meta.relationshipOrder.filter((id) => id !== relId)
}

function applyRelOrderSet(meta: MetadataV2, op: DraftOp) {
  meta.relationshipOrder = [...(op.order ?? [])]
}

function applyGroupUpsert(meta: MetadataV2, op: DraftOp) {
  const groupId = requireString(op, 'id')
  if (!meta.groups[groupId]) {
    meta.groups[groupId] = { id: groupId, name: '', tableIds: [] }
    appendOnce(meta.groupOrder, groupId)
  }
  meta.groups[groupId] = { ...meta.groups[groupId], ...(op.fields ?? {}) }
}

function applyGroupRemove(meta: MetadataV2, op: DraftOp) {
  const groupId = requireString(op, 'id')
  if (!meta.groups[groupId]) return
  delete meta.groups[groupId]
  meta.groupOrder = meta.groupOrder.filter((id) => id !== groupId)
}

function applyGroupOrderSet(meta: MetadataV2, op: DraftOp) {
  meta.groupOrder = [...(op.order ?? [])]
}

function appendOnce(order: string[], itemId: string) {
  if (!order.includes(itemId)) order.push(itemId)
}

function requireTable(meta: MetadataV2, tableId?: string) {
  if (!tableId || !meta.tables[tableId]) throw new Error(`Table '${tableId}' not found.`)
  return meta.tables[tableId]
}

function requireString(op: DraftOp, fieldName: 'id') {
  const value = op[fieldName]
  if (!value || typeof value !== 'string') throw new Error(`Field '${fieldName}' is required.`)
  return value
}
